import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getSyncState, subscribeSync } from '../hooks/useFirestoreSyncMonitor';
import { isSaveFailing, subscribeSaveHealth } from '../lib/saveHealth';
// The routes where this hint can never show (see onMarketingRoute below);
// shared with StaleConnectionBanner since 2026-09-06.
import { isMarketingPath } from '../lib/marketingPaths';

/**
 * ExtensionBlockHint — proactive detection for ad-blocker extensions that
 * block Firestore's WebChannel transport, paired with a soft top banner.
 *
 * Walter 2026-05-28: see the BACKLOG entry from 2026-05-26 for the original
 * diagnosis. Ghostery, uBlock Origin, Brave Shields, AdGuard et al. catch
 * URLs of the form `firestore.googleapis.com/.../channel?...&zx=...`
 * because the `?zx=` cache-buster matches filter patterns meant for
 * analytics endpoints. When the WebChannel is blocked, Firestore reads
 * stall silently — pages render blank, deletes look like "did nothing,"
 * realtime sync drops. Console shows ERR_BLOCKED_BY_CLIENT but no JS
 * exception fires, so the SDK can't surface it cleanly. We have to detect
 * it ourselves.
 *
 * Detection: fetch a tiny WebChannel-shaped URL on app start. If the
 * BROWSER blocks the request, fetch() rejects with a TypeError before
 * the network even sees it — that's the signature. Any other outcome
 * (success, HTTP error, CORS error, even server-side 4xx) means the
 * request reached the wire, so an extension isn't blocking. (We do NOT
 * care what the server says — we don't even read the response.)
 *
 * Banner: soft amber top-pinned bar, dismissable, persistent dismissal
 * via localStorage. We avoid hammering the user — once dismissed it
 * stays dismissed for this device. If they later realize sync is broken,
 * they can clear localStorage or open Incognito.
 *
 * Marketing pages: suppressed (no Firestore there, no point hinting).
 */

// Walter 2026-05-28: two-tier dismissal. The original single
// "dismissed forever" flag was friendly only to users who already knew
// what the banner meant. F&F testers might × it on reflex and then
// silently lose real-time sync. Now ×-dismiss snoozes 24h; an
// explicit "never show again" link does the permanent dismiss. The
// legacy flag below is honoured for back-compat (existing dismissed
// users stay dismissed forever rather than getting nagged tomorrow).
const SNOOZE_UNTIL_KEY = 'lists_extension_block_snoozed_until';
const PERMANENT_KEY = 'lists_extension_block_dismissed_permanent';
const LEGACY_KEY = 'lists_extension_block_dismissed';
const SNOOZE_MS = 24 * 60 * 60 * 1000;

const dismissButtonStyle = {
  background: 'transparent',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.35)',
  borderRadius: 4,
  padding: '4px 8px',
  cursor: 'pointer',
  fontFamily: 'var(--mono)',
  fontSize: 'calc(10px * var(--ui-scale, 1))',
  whiteSpace: 'nowrap',
};
// Probe URL is intentionally a Firestore WebChannel-shaped path with the
// `zx` query param that triggers ad-blocker heuristics. The path itself
// doesn't need to be valid — we only care whether the request leaves the
// browser. A `HEAD` keeps it lightweight; mode no-cors so the response
// status is opaque (we don't read it). Fresh `zx` per attempt so nothing
// is served from cache and each retry is a genuine network attempt.
function buildProbeUrl() {
  // Walter's QA pass 2026-08-25: name THIS build's project, not a hardcoded
  // "walterstodotool". The path doesn't have to be valid for the probe to
  // work, but a dev build firing requests that name the PROD database is the
  // kind of thing that costs an hour when someone reads the network tab — I
  // lost a day to exactly that class of confusion on 2026-08-20.
  const project = process.env.REACT_APP_FIREBASE_TARGET === 'dev'
    ? 'walterstodotool-dev'
    : 'walterstodotool';
  return (
    'https://firestore.googleapis.com/google.firestore.v1.Firestore/Listen/channel?VER=8&database=projects%2F'
    + project
    + '%2Fdatabases%2F(default)&RID=rpc&SID=probe&CI=0&AID=0&TYPE=xmlhttp&zx='
    + Math.random().toString(36).slice(2, 10)
  );
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// Module-singleton state so all consumers see the same detection result
// without re-probing. The probe runs once per page load.
let probeStatus = 'pending'; // 'pending' | 'blocked' | 'clear'
let probePromise = null;
const status$ = new Set(); // subscribers for status changes

// Walter 2026-05-28: emit a single console.info when we detect a
// blocked state so anyone with DevTools open finds the explanation
// right next to the ERR_BLOCKED_BY_CLIENT errors Chrome logs from the
// network layer. Without this, a curious user (or F&F tester
// reporting bugs) sees a wall of red Firestore failures and has no
// way to connect them to the cause. Fired once per page load — we
// don't repeat on every flip back to blocked.
let blockedLogEmitted = false;
function setStatus(next) {
  if (probeStatus === next) return;
  probeStatus = next;
  if (next === 'blocked' && !blockedLogEmitted) {
    blockedLogEmitted = true;
    try {
      console.info(
        '[listicity] real-time sync is impaired — likely by ad blocker. whitelist listicity.app to fix.'
      );
    } catch { /* ignore */ }
  }
  for (const fn of status$) fn(next);
}

// Walter 2026-05-31: REMOVED a PerformanceObserver-based secondary
// heuristic that flagged any `firestore.googleapis.com` resource-timing
// entry with `transferSize === 0` as "blocked." It produced FALSE
// POSITIVES on clean devices (Walter saw the banner on mobile with no
// ad blocker; it also fired in a clean local test browser). Root cause:
// per the Resource Timing Level 2 spec, cross-origin responses WITHOUT a
// `Timing-Allow-Origin` header report `transferSize`, `encodedBodySize`
// and `decodedBodySize` as 0 regardless of success. Firestore doesn't
// send TAO for arbitrary app origins, and its `Listen` channel is a
// long-poll that legitimately stays open with no observable bytes — so
// `transferSize === 0` is true for essentially every healthy Firestore
// connection. The signal was indistinguishable from a real block.
//
// Detection relies on the `fetch` probe below: an actual browser-
// extension block rejects fetch() with a TypeError BEFORE the request
// reaches the network — a clean, specific signature.
//
// Walter 2026-05-31: BUT a single early probe is NOT enough. The page is
// a PWA controlled by a service worker, and at page-load time a probe can
// reject with a transient TypeError — SW still activating, connection
// cold — even on a perfectly clean device with no ad blocker. Verified
// in a clean test browser: the first probe rejected, then 4 immediate
// replays all resolved. Latching "blocked" on that first transient is a
// false positive (this is what fired on Walter's mobile).
//
// Fix: (1) wait for the SW to settle before probing; (2) RETRY on
// failure. A real ad blocker blocks EVERY attempt deterministically; a
// startup race recovers on the next try. We only conclude "blocked"
// after several consecutive hard failures. Better to under-report than
// to scare a clean-device user into thinking their data is broken.

// A control URL that exercises the EXTERNAL network but is never on any
// ad-blocker filter list — Chrome itself uses gstatic generate_204 for
// connectivity checks. If this fails too, the problem is the network
// (offline, cold start, sandbox), NOT an extension, and we must stay
// quiet. Fresh query each call to defeat caching.
function buildControlUrl() {
  return 'https://www.gstatic.com/generate_204?z=' + Math.random().toString(36).slice(2, 8);
}

// Probe a URL and report whether it reached the wire AND how fast it
// resolved/rejected. `reached: true` = success OR timeout (slow network
// still means the request left the browser). `reached: false` = a
// TypeError/NetworkError. `ms` = time to settle, the key discriminator:
// an ad-blocker block rejects near-INSTANTLY (the request never leaves
// the browser — ERR_BLOCKED_BY_CLIENT is synchronous), whereas a cold or
// failing network rejects only after TCP/TLS attempts (hundreds of ms+).
async function reachesWire(url, method = 'HEAD') {
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const elapsed = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
  try {
    await fetch(url, {
      method,
      mode: 'no-cors',
      cache: 'no-store',
      signal: AbortSignal.timeout ? AbortSignal.timeout(4000) : undefined,
    });
    return { reached: true, ms: elapsed() };
  } catch (err) {
    if (err && err.name === 'AbortError') return { reached: true, ms: elapsed() }; // slow but reached
    return { reached: false, ms: elapsed() };
  }
}

// A real extension block rejects synchronously. Cold-start / transient
// network failures take far longer. 250ms is a generous ceiling for the
// "instant" signature while staying well below real connection latency.
const INSTANT_REJECT_MS = 250;

// One probe attempt, comparing the firestore-shaped request against the
// control. We flag a block ONLY when ALL of: firestore did NOT reach the
// wire, the control DID (so the network works), AND the firestore
// rejection was near-instant (the ad-blocker signature). Everything else
// — firestore reachable, network down, or a slow rejection — is 'clear'.
async function probeOnce() {
  // Walter 2026-06-13: probe with POST, not HEAD. The real Firestore
  // WebChannel request that extensions block is a POST to /Listen/channel;
  // our old HEAD probe sailed through (reached the server, got a 400) even
  // when the real POST was blocked, so we declared "clear" and never warned.
  // Match the method the filters actually catch. Control stays HEAD (gstatic
  // generate_204 answers either; we only care that it left the browser).
  const [fs, ctrl] = await Promise.all([
    reachesWire(buildProbeUrl(), 'POST'),
    reachesWire(buildControlUrl()),
  ]);
  if (fs.reached) return 'clear';            // firestore reachable → not blocked
  if (!ctrl.reached) return 'clear';         // network itself down/cold → not an ad blocker
  if (fs.ms > INSTANT_REJECT_MS) return 'clear'; // slow rejection → network, not a block
  return 'fail';                             // instant firestore reject while network works → block
}

const PROBE_ATTEMPTS = 3;   // consecutive hard failures required to flag
const PROBE_RETRY_MS = 1200; // gap between attempts — lets a startup race clear

function runProbe() {
  if (probePromise) return probePromise;
  probePromise = (async () => {
    // Let the service worker finish activating so we don't catch an
    // activation-race transient. Capped so a missing/slow SW can't stall
    // detection.
    try {
      const sw = navigator.serviceWorker;
      if (sw && sw.ready) await Promise.race([sw.ready, delay(2500)]);
    } catch { /* ignore */ }

    for (let i = 0; i < PROBE_ATTEMPTS; i++) {
      const result = await probeOnce();
      if (result === 'clear') {
        if (probeStatus === 'pending') setStatus('clear');
        return;
      }
      // 'fail' — retry unless this was the last attempt.
      if (i < PROBE_ATTEMPTS - 1) await delay(PROBE_RETRY_MS);
    }
    // Every attempt failed with a network-level rejection → genuinely
    // blocked (a transient race would have cleared on one of the retries).
    setStatus('blocked');
  })();
  return probePromise;
}

// Read current dismissal state — permanent flag wins, then legacy
// (treated as permanent for back-compat), then snooze-until timestamp.
function readDismissalState() {
  try {
    if (localStorage.getItem(PERMANENT_KEY) === '1') return 'permanent';
    if (localStorage.getItem(LEGACY_KEY) === '1') return 'permanent';
    const until = parseInt(localStorage.getItem(SNOOZE_UNTIL_KEY) || '0', 10);
    if (until && Date.now() < until) return 'snoozed';
    return 'active';
  } catch {
    return 'active';
  }
}

export default function ExtensionBlockHint() {
  const [status, setStatus] = useState(probeStatus);
  // Ground-truth connection health from the Firestore sync monitor (B).
  const [sync, setSync] = useState(() => ({ ...getSyncState() }));
  const [dismissalState, setDismissalState] = useState(readDismissalState);
  // Walter 2026-08-20: the BEHAVIOURAL signal. The probe below predicts how a
  // blocker behaves; Brave Shields didn't match its signature, so this banner
  // stayed silent through a full day of his edits failing to leave the device
  // (console full of ERR_BLOCKED_BY_CLIENT on firestore.googleapis.com). This
  // one watches the symptom instead — repeated save failures — so it fires
  // whatever the cause and cannot be evaded by a blocker we didn't anticipate.
  const [savesFailing, setSavesFailing] = useState(() => isSaveFailing());
  useEffect(() => subscribeSaveHealth(setSavesFailing), []);
  const location = useLocation();

  useEffect(() => {
    // Subscribe to probe status changes (A) — the fetch probe is async and
    // may resolve after this component mounts.
    const onChange = (next) => setStatus(next);
    status$.add(onChange);
    // Walter's QA pass 2026-08-25: don't probe on the marketing routes. The
    // banner is suppressed there anyway (onMarketingRoute below), so the two
    // requests bought nothing and put two red 400s in the console of the
    // PUBLIC landing page — the first thing anyone opening DevTools on
    // listicity.app would see.
    if (isMarketingPath(window.location.pathname)) return () => { status$.delete(onChange); };
    if (probeStatus === 'pending') runProbe();
    else setStatus(probeStatus);
    return () => { status$.delete(onChange); };
  }, []);

  useEffect(() => {
    // Subscribe to the Firestore sync monitor (B, ground truth):
    // connectionBlocked = real WebChannel dead past the grace window;
    // everSynced = server data has flowed at least once this session.
    const onSync = () => setSync({ ...getSyncState() });
    const unsub = subscribeSync(onSync);
    onSync();
    return unsub;
  }, []);

  // Suppress on marketing pages — Firestore isn't loaded there, so even
  // if an extension blocks WebChannel it doesn't affect those routes.
  const onMarketingRoute = isMarketingPath(location.pathname);

  // Show when the real connection is confirmed blocked (B, the watchdog —
  // which is itself gated on never-having-synced) OR the fast-path probe
  // flagged it (A).
  //
  // Walter 2026-06-13: do NOT gate this on `!everSynced`. With Ghostery,
  // SOME Firestore requests sneak through (the 400s in the console) while
  // the realtime Listen channel is blocked — so a single server read can
  // flip `everSynced` true and was wrongly SUPPRESSING the banner even
  // though the probe correctly saw the block. The probe POSTs to the exact
  // blocked URL pattern, so its "blocked" verdict is trustworthy on its own
  // (a clean connection makes the probe reach the wire → "clear", so this
  // can't false-positive on a healthy app).
  const blocked = sync.connectionBlocked || status === 'blocked' || savesFailing;

  if (!blocked || dismissalState !== 'active' || onMarketingRoute) return null;

  const snooze = () => {
    try { localStorage.setItem(SNOOZE_UNTIL_KEY, String(Date.now() + SNOOZE_MS)); } catch {}
    setDismissalState('snoozed');
  };
  const dismissForever = () => {
    try { localStorage.setItem(PERMANENT_KEY, '1'); } catch {}
    setDismissalState('permanent');
  };

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1800,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        padding: '10px',
      }}
    >
      <div
        style={{
          maxWidth: 640,
          width: '100%',
          background: 'rgba(232, 98, 44, 0.96)', // accent-toned amber
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 'var(--radius)',
          padding: '10px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          lineHeight: 1.45,
          pointerEvents: 'auto',
        }}
      >
        {/* Row 1: icon + explanatory text. Walter 2026-05-28: switched
            from horizontal (icon | text | buttons) to vertical (text on
            top, buttons in their own row below). The horizontal layout
            squeezed the buttons into a narrow gutter and read as
            cramped at typical banner widths. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1.5 }}>⚠</span>
          <span style={{ flex: 1 }}>
            {savesFailing
              ? 'your recent edits are saved on this device but are NOT reaching the cloud. '
              : "a browser extension (Ghostery, uBlock, Brave Shields, etc.) is blocking listicity's database connection. real-time sync may break. "}
            {savesFailing
              ? 'a browser extension (Ghostery, uBlock, Brave Shields, etc.) is the usual cause. '
              : ''}
            <strong style={{ fontWeight: 600 }}>
              whitelist listicity.app
            </strong>{' '}
            in your extension to fix.
          </span>
        </div>
        {/* Row 2: actions, right-aligned. Two-tier dismiss — "remind me
            tomorrow" is the friendly default; "don't remind me" is the
            heavier permanent-dismiss for users who know their setup. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={snooze}
            title="hide the banner for 24 hours"
            style={dismissButtonStyle}
          >
            remind me tomorrow
          </button>
          <button
            type="button"
            onClick={dismissForever}
            title="don't show this banner again on this device"
            style={{ ...dismissButtonStyle, opacity: 0.75 }}
          >
            don't remind me
          </button>
        </div>
      </div>
    </div>
  );
}
